import { useEffect, useMemo, useRef, useState, type ComponentRef, type ReactNode } from "react";
import { Platform, Pressable, type GestureResponderEvent } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon, type IconNameType } from "@src/components/Icon";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import type { NavSidePanelItemRenderProps } from "@visioni/a3s-ui";
import { SnapshotImage } from "@visioni/a3s-media/app";
import { LegacySnapshotImage } from "@src/features/cameras/components/LegacySnapshotImage";
import { useCameraArmedStatus } from "@src/features/cameras/hooks/useCameraArmedStatus";
import { formatCameraLabel, getCameraKeyString } from "@src/utils/key-parser";
import { type CameraNavItemData, type SiteNavItemData } from "@src/features/cameras/hooks/useCameraSideNavItems";
import { NavPressable } from "@src/components/NavPressable";
import { sizes } from "@src/constants/sizes";

interface CameraItemRowProps {
  readonly data: CameraNavItemData;
  readonly selected: boolean;
  readonly minimized: boolean;
  readonly onPress: () => void;
}

function CameraItemRow({ data, selected, minimized, onPress }: CameraItemRowProps) {
  const { theme } = useUnistyles();
  const { camera, snapshot, siteId } = data;
  const { isArmed } = useCameraArmedStatus({ camera, siteId });
  const cameraKey = `${camera.key.asset}@${camera.key.id}`;
  const normalizedKey = getCameraKeyString(camera);
  const displayName = camera.name || formatCameraLabel(normalizedKey);
  const subtitleName = camera.name ? formatCameraLabel(normalizedKey) : undefined;

  const rowRef = useRef<ComponentRef<typeof View>>(null);

  // Scroll the selected camera into view so its asset-mates (which sort next
  // to it) are visible without the operator having to hunt through the list.
  useEffect(() => {
    if (selected && Platform.OS === "web" && rowRef.current) {
      (rowRef.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [selected]);

  const href = data.href as never;

  const statusBadge = useMemo(() => {
    if (isArmed) {
      return {
        label: "Armed",
        iconName: "lockClosed" as IconNameType,
        backgroundColor: theme.colors.purple[600],
        borderColor: theme.colors.purple[300],
      };
    }
    return {
      label: "Disarmed",
      iconName: "lockOpen" as IconNameType,
      backgroundColor: theme.colors.warning[700],
      borderColor: theme.colors.warning[400],
    };
  }, [isArmed, theme]);

  if (minimized) {
    return (
      <View ref={rowRef}>
        <NavPressable
          onPress={onPress}
          href={href}
          accessibilityLabel={displayName}
          style={[
            styles.itemContainer,
            styles.itemRowMinimized,
            selected && { backgroundColor: theme.colors.surface2.selected },
          ]}
        >
          <View style={styles.thumbnailWrapperMinimized}>
            {snapshot ?
              <SnapshotImage
                snapshot={snapshot}
                width={400}
                style={styles.thumbnailWrapperMinimized}
                placeholderColor={theme.colors.borderCard}
              />
            : <LegacySnapshotImage
                cameraKey={cameraKey}
                thumbnailUrl={camera.snapshot?.url}
                capturedAt={camera.snapshot?.capturedAt}
                style={styles.thumbnailWrapperMinimized}
              />
            }
          </View>
        </NavPressable>
      </View>
    );
  }

  return (
    <View ref={rowRef}>
      <NavPressable onPress={onPress} href={href} accessibilityLabel={displayName} style={styles.itemContainer}>
        <View style={[styles.itemRow, selected && { backgroundColor: theme.colors.surface2.selected }]}>
          <View style={styles.thumbnailWrapper}>
            {snapshot ?
              <SnapshotImage
                snapshot={snapshot}
                width={400}
                style={styles.thumbnailWrapper}
                placeholderColor={theme.colors.borderCard}
              />
            : <LegacySnapshotImage
                cameraKey={cameraKey}
                thumbnailUrl={camera.snapshot?.url}
                capturedAt={camera.snapshot?.capturedAt}
                style={styles.thumbnailWrapper}
              />
            }
          </View>
          <View gap="xs" style={styles.cameraInfo}>
            <Text textStyle="sm/regular" numberOfLines={1} style={styles.ellipsisText}>
              {toSentenceCase(displayName)}
            </Text>
            {subtitleName && (
              <Text textStyle="xxs/regular" numberOfLines={1} style={[styles.textSecondary, styles.ellipsisText]}>
                {subtitleName}
              </Text>
            )}
            <View
              style={[
                styles.statusBadge,
                { backgroundColor: statusBadge.backgroundColor, borderColor: statusBadge.borderColor },
              ]}
            >
              <Icon name={statusBadge.iconName} size={14} color={theme.colors.white.default} />
              <Text textStyle="xxs/regular" style={styles.textBadge}>
                {statusBadge.label}
              </Text>
            </View>
          </View>
        </View>
      </NavPressable>
    </View>
  );
}

interface SiteItemRowProps {
  readonly data: SiteNavItemData;
  readonly expanded: boolean;
  readonly minimized: boolean;
  readonly selected: boolean;
  readonly onPress: (event: import("react-native").GestureResponderEvent) => void;
}

function SiteItemRow({ data, expanded, minimized, selected, onPress }: SiteItemRowProps) {
  const { theme } = useUnistyles();
  const { siteName, siteSubtitle, thumbnailUrl } = data;
  const [imageError, setImageError] = useState(false);

  if (minimized) {
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityState={{ expanded, selected }}
        style={({ pressed }) => [
          styles.itemRow,
          styles.itemRowMinimized,
          selected && { backgroundColor: theme.colors.surface2.selected },
          { opacity: pressed ? 0.72 : 1 },
        ]}
      >
        <Icon name="domain" size={20} color={theme.colors.icon} />
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ expanded, selected }}
      style={({ pressed }) => [
        styles.itemRow,
        selected && { backgroundColor: theme.colors.surface2.selected },
        { opacity: pressed ? 0.72 : 1 },
      ]}
    >
      <View style={styles.siteThumbnailWrapper}>
        {thumbnailUrl && !imageError ?
          <img
            src={thumbnailUrl}
            alt={siteName}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        : <Icon name="domain" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />}
      </View>
      <View flex={1} gap="xxs">
        <Text textStyle="sm/bold" color="text" numberOfLines={1}>
          {siteName}
        </Text>
        {siteSubtitle && (
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            {siteSubtitle}
          </Text>
        )}
      </View>
      <Icon name={expanded ? "chevronUp" : "chevronDown"} size={theme.spacing["6xl"]} color={theme.colors.icon} />
    </Pressable>
  );
}

export interface CameraNavRendererProps {
  readonly itemDataMap: ReadonlyMap<string, CameraNavItemData | SiteNavItemData>;
}

/**
 * Returns a `renderItem` function for NavSidePanel. Camera/site items get
 * custom row rendering (thumbnail, badge, etc). All other items fall through
 * to the panel's default element.
 */
export function useCameraNavItemRenderer({ itemDataMap }: CameraNavRendererProps) {
  return useMemo(() => {
    return function renderNavItem(props: NavSidePanelItemRenderProps): ReactNode {
      const data = itemDataMap.get(props.item.id);
      if (!data) return props.defaultElement;

      if ("camera" in data) {
        return (
          <CameraItemRow
            data={data}
            selected={props.selected}
            minimized={props.minimized}
            onPress={() => props.item.onPress?.({} as GestureResponderEvent)}
          />
        );
      }
      return (
        <SiteItemRow
          data={data}
          expanded={props.expanded}
          minimized={props.minimized}
          selected={props.selected}
          onPress={props.onPress}
        />
      );
    };
  }, [itemDataMap]);
}

const styles = StyleSheet.create((theme) => ({
  itemContainer: {
    borderRadius: theme.spacing.md,
    overflow: "hidden",
  },
  itemRow: {
    minHeight: 44,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
  itemRowMinimized: {
    minHeight: 44,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    justifyContent: "center",
    alignItems: "center",
  },
  thumbnailWrapper: {
    width: 80,
    height: 56,
    borderRadius: theme.spacing.sm,
    overflow: "hidden",
    flexShrink: 0,
  },
  thumbnailWrapperMinimized: {
    width: 36,
    height: 26,
    borderRadius: theme.spacing.xs,
    overflow: "hidden",
  },
  siteThumbnailWrapper: {
    width: 82,
    height: 52,
    minHeight: 52,
    borderRadius: sizes.radiusSm,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.muted,
  },
  cameraInfo: {
    flex: 1,
    minWidth: 0,
  },
  ellipsisText: {
    ...(Platform.OS === "web" ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const } : {}),
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.spacing["3xl"],
    borderWidth: 1,
    height: 26,
  },
  textBadge: {
    color: theme.colors.white.default,
  },
  textSecondary: {
    color: theme.colors.surface1.foregroundSecondary,
  },
}));
